import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {PosterEditingBaseFontSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-font-selector';
import type {BrandFont} from '@Components/editor-brands/editor-brands.types';
import type {FontData} from '@Libraries/font-library';
import {isInternalFont, USER_FONT_LICENSE} from '@Libraries/font-library';
import {onAddBrandAssetClickedHandler} from '@Components/poster-editor/library/poster-editor-library';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {FontFamilyOptionType} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-with-dual-font-options/item-with-dual-font-options.types';
import type {DeepPartial} from '@/global';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromTextStylesActiveItem} from '@Hooks/poster-editor/useGetKeyFromTextStylesActiveItem';
import {useGetKeyFromTableItem} from '@Hooks/poster-editor/useGetKeyFromTableItem';

interface MenuItemFontSelectorProps {
  font?: FontFamilyOptionType;
  onBack?(): void;
  onClose?(): void;
  showCloseButton?: boolean;
  showBackButton?: boolean;
  headerText?: string;
  isForMobile?: boolean;
}

export function ItemWithDualFontsSelector({
  font = FontFamilyOptionType.ONE,
  onClose = (): void => {},
  onBack = (): void => {},
  showCloseButton = false,
  showBackButton = true,
  isForMobile = false,
  ...props
}: MenuItemFontSelectorProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const itemFontFamily = useGetKeyFromTextStylesActiveItem('fontFamily');
  const itemFontLicense = useGetKeyFromTextStylesActiveItem('fontLicense');
  const itemFontFamily2 = useGetKeyFromTableItem('fontFamily2');

  const [temporaryState, setTemporaryState] = useState<string>('');
  const [isNewFontLoading, setIsNewFontLoading] = useState(false);

  if (!activeItem || (activeItem && !('textStyles' in activeItem)) || itemFontFamily === undefined || itemFontLicense === undefined) {
    throw new Error('Item does not exist or text styles property does not exist in active item');
  }

  if (!('fontFamily2' in activeItem) || itemFontFamily2 === undefined) {
    throw new Error('Item does not have font family 2 property');
  }

  const getObjectToUpdate = (fontFamily: string): DeepPartial<ItemObject> => {
    const fontLicense = isInternalFont(fontFamily) ? '' : USER_FONT_LICENSE;
    if (font === FontFamilyOptionType.TWO) {
      return {
        textStyles: {fontLicense},
        fontFamily2: fontFamily,
      };
    }
    return {
      textStyles: {
        fontFamily,
        fontLicense,
      },
    };
  };

  const onSelectFontFamily = (fontFamily: string): void => {
    setIsNewFontLoading(true);
    setTemporaryState(fontFamily);

    const oldFontFamily = font === FontFamilyOptionType.TWO ? itemFontFamily2 : itemFontFamily;
    const oldFontLicense = itemFontLicense;
    activeItem
      .updateFromObject(getObjectToUpdate(fontFamily))
      .then((): void => {})
      .catch((): void => {
        if (font === FontFamilyOptionType.TWO) {
          activeItem.fontFamily2 = oldFontFamily;
        } else {
          activeItem.textStyles.fontFamily = oldFontFamily;
        }
        activeItem.textStyles.fontLicense = oldFontLicense;
        setTemporaryState(itemFontFamily);
      })
      .finally((): void => {
        setIsNewFontLoading(false);
      });
  };

  const getFontToShow = (): string => {
    switch (font) {
      case FontFamilyOptionType.ONLY_FONT:
      case FontFamilyOptionType.ONE:
        return itemFontFamily;
      case FontFamilyOptionType.TWO:
        return itemFontFamily2;
      default:
        throw new Error(`unhandled font option`);
    }
  };

  return (
    <PosterEditingBaseFontSelector
      fontsInDesign={window.posterEditor?.whiteboard?.getFontsOnPoster() ?? []}
      onBrandItemClicked={(brand: BrandFont): void => {
        onSelectFontFamily(brand.fontFamily);
      }}
      isSelectedFontLoading={isNewFontLoading}
      selectedFontFamily={isNewFontLoading ? temporaryState : getFontToShow()}
      onSelectFontFamily={(selectedFont: FontData): void => {
        onSelectFontFamily(selectedFont.fontFamily);
      }}
      showHeader
      onBackButtonCallback={(): void => {
        onBack();
      }}
      onAddBrandAsset={(): void => {
        void onAddBrandAssetClickedHandler();
      }}
      onCloseButtonCallback={(): void => {
        onClose();
      }}
      showBackButton={showBackButton}
      showCloseButton={showCloseButton}
      headerText={props.headerText}
      hasSmallTopPadding={isForMobile}
    />
  );
}
